import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { toast } from '@/hooks/use-toast'

/** How long the copied id stays highlighted before the styling returns to normal. */
const COPIED_FEEDBACK_MS = 1500

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timeout.current) clearTimeout(timeout.current)
  }, [])

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(value)
      if (timeout.current) clearTimeout(timeout.current)
      timeout.current = setTimeout(() => setCopied(null), COPIED_FEEDBACK_MS)
    } catch {
      // Clipboard access is denied outside a secure context, which is every plain-http
      // preview build — say so rather than leaving the click looking ignored.
      toast({ variant: 'destructive', description: 'Could not copy to clipboard' })
    }
  }

  return { copied, copy }
}

/**
 * One id inside the path, copyable on its own.
 *
 * The ids are what gets pasted into the AWS console or an s3 command, and picking one out of a
 * hundred-character path by hand is the part that goes wrong — so each is its own click target.
 */
function CopyableId({
  value,
  label,
  copied,
  onCopy,
}: {
  value: string
  label: string
  copied: string | null
  onCopy: (value: string) => void
}) {
  const isCopied = copied === value

  return (
    <button
      type="button"
      onClick={() => onCopy(value)}
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className={
        isCopied
          ? 'font-mono text-emerald-700'
          : 'font-mono text-indigo-600 hover:text-indigo-800 hover:underline'
      }
    >
      {value}
    </button>
  )
}

/**
 * Reference details about the workflow that are looked up occasionally rather than read on
 * every visit — collapsed by default so they stay out of the way of the settings above.
 */
export function WorkflowInfo({
  organizationId,
  workflowId,
}: {
  organizationId: string
  workflowId: string
}) {
  const [expanded, setExpanded] = useState(false)
  const { copied, copy } = useCopy()

  return (
    <div className="mt-6 border-t border-on-canvas/20 pt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wide text-on-canvas-muted">
          Workflow info
        </p>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-on-canvas-muted" />
          : <ChevronDown className="h-3.5 w-3.5 text-on-canvas-muted" />}
      </button>
      {expanded && (
        <div className="mt-1.5 space-y-1 text-xs text-on-canvas-muted">
          {/*
            Where the workflow's extraction examples live in the order system bucket. Mirrors
            the layout in order-system (buckets/<stage>/organizations/<org>/workflows/<workflow>/
            examples/.pdf/); the stage is the bucket itself, frontflow-order-system-<stage>, so
            it is not part of the key. Written out segment by segment rather than as one string
            so each id can be its own click target.
          */}
          <p className="break-all">
            <span className="text-on-canvas-muted">Extraction examples path: </span>
            <span className="font-mono text-on-canvas">
              organizations/
              <CopyableId
                value={organizationId}
                label="organization id"
                copied={copied}
                onCopy={copy}
              />
              /workflows/
              <CopyableId value={workflowId} label="workflow id" copied={copied} onCopy={copy} />
              /examples/.pdf/
            </span>
          </p>
        </div>
      )}
    </div>
  )
}
